/**
 * Client-side display formatters. All functions are pure and defensive: they
 * accept loosely-typed values (DTO money fields arrive as strings) and return a
 * safe placeholder ("—") for null/undefined/invalid input instead of throwing.
 * User-facing output is localized to Vietnamese (vi-VN).
 */

/** Value that can reasonably be coerced to a number for display. */
export type NumericLike = number | string | null | undefined;

const PLACEHOLDER = '—';

/**
 * Coerce a loosely-typed numeric value to a finite number, or null if it cannot
 * be represented (empty string, NaN, Infinity, nullish).
 */
function toFiniteNumber(value: NumericLike): number | null {
  if (value === null || value === undefined || value === '') return null;
  const n = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(n) ? n : null;
}

/**
 * Format a monetary amount using the given ISO currency code (default VND).
 * Intl decides the number of fraction digits per currency (VND -> 0).
 */
export function formatCurrency(value: NumericLike, currency = 'VND'): string {
  const n = toFiniteNumber(value);
  if (n === null) return PLACEHOLDER;
  try {
    return new Intl.NumberFormat('vi-VN', { style: 'currency', currency }).format(n);
  } catch {
    // Unknown currency code — fall back to a grouped number + suffix.
    return `${formatNumber(n)} ${currency}`;
  }
}

/** Format a number with thousands separators (vi-VN grouping). */
export function formatNumber(value: NumericLike): string {
  const n = toFiniteNumber(value);
  if (n === null) return PLACEHOLDER;
  return new Intl.NumberFormat('vi-VN').format(n);
}

/** Format an ISO date string as a short date (dd/MM/yyyy). */
export function formatDate(iso: string | null | undefined): string {
  if (!iso) return PLACEHOLDER;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return PLACEHOLDER;
  return d.toLocaleDateString('vi-VN', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

/** Format an ISO date string as date + time (dd/MM/yyyy HH:mm). */
export function formatDateTime(iso: string | null | undefined): string {
  if (!iso) return PLACEHOLDER;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return PLACEHOLDER;
  return d.toLocaleString('vi-VN', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/** Strip the Facebook `act_` prefix so only the numeric id is shown. */
export function adAccountNumber(idFb: string | null | undefined): string {
  return (idFb ?? '').replace(/^act_/, '');
}

/** Ads Manager campaigns URL for an ad account (opens the account directly). */
export function adsManagerUrl(idFb: string | null | undefined): string {
  return `https://adsmanager.facebook.com/adsmanager/manage/campaigns?act=${adAccountNumber(idFb)}`;
}

/**
 * Facebook billing/payment settings URL for an ad account — where the user adds
 * or verifies a payment card. Cards CANNOT be added via API (PCI): this opens
 * Facebook's own secure payment page for that exact account.
 */
export function paymentSettingsUrl(idFb: string | null | undefined): string {
  return `https://business.facebook.com/ads/manager/account_settings/account_billing/?act=${adAccountNumber(idFb)}&tab=account_billing_settings`;
}

/**
 * Format a fraction as a percentage. Input is a fraction (0.2 => "20%").
 * Pass an already-scaled value divided by 100 if you have a percent number.
 */
export function formatPercent(fraction: NumericLike): string {
  const n = toFiniteNumber(fraction);
  if (n === null) return PLACEHOLDER;
  return new Intl.NumberFormat('vi-VN', {
    style: 'percent',
    maximumFractionDigits: 2,
  }).format(n);
}
